import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FeatureFlag } from '@app/enums';
import { Benefit } from '@app/models/payroll/benefit.model';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { Deduction } from '@app/models/payroll/deduction.model';
import { Ytd } from '@app/models/payroll/ytd.model';
import { HandsOnTableView } from '@app/modules/common/views';
import { YtdColumnSettings } from '@app/modules/payroll/interfaces/ytd-column-settings.interface';
import { YTDHeader } from '@app/modules/payroll/interfaces/ytd-header.interface';
import {
    BulkUpdate,
    BulkUpdateResponse,
    Column,
    Row,
    YtdTable,
} from '@app/modules/payroll/interfaces/ytd-table.interface';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { PayrollService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { NumericString } from '@app/types/numeric-string.type';
import { Locale, TranslatableKey } from '@app/types/translatable.type';
import { formatLocalizedNumber } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import {
    HandsontableableComponent,
    convertEmptyAndNegativesToZero,
} from '@payroll/views/record/utils/ytds/handsontable-helpers';
import { buildColumns, buildDataset, getItemFromYtd } from '@payroll/views/record/utils/ytds/ytd-table-helpers';
import { ColDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { format, isValid, parse, startOfYear } from 'date-fns';
import { CellChange as HandsOnTableCellChange } from 'handsontable/common';
import { debounce } from 'lodash-es';
import { CustomHeader } from './custom-header.component';

const BULK_UPDATE_BATCH_SIZE_LIMIT = 5000;
const BULK_UPDATE_DEBOUNCE_TIME = 1000;
const FINAL_PAY_PERIOD_STORAGE_KEY = 'savedFinalPayPeriod';

/**
 * Row type for v1 of the ag-grid implementation.
 * V2 uses the Row interface
 */
type AgGridDatum = {
    id: NumericString;
    employeeName: string;
    [key: string]: string | number;
};

/**
 * Object type, currently used for tracking concurrent update operations in v1,
 * as well as cell errors in both v1 and v2.
 */
type AgGridCell = {
    rowId: string;
    columnId: string;
    message?: string | TranslatableKey;
};

// Augmented change type with an additional row id (for ag-grid)
// This stuff will go away when we implement bulk update for the ag-grid table
type AgGridCellChange = [...HandsOnTableCellChange, string];

type CellChange = HandsOnTableCellChange | AgGridCellChange;

@Component({
    templateUrl: './ytd-index.template.html',
    styleUrls: ['./ytd-index.style.scss'],
})
export class YtdIndexView extends HandsOnTableView implements OnInit, HandsontableableComponent {
    isLoading = true;
    benefitsAreSetUp = true;
    payrollHasBeenPaid = true;
    dismissedBenefitsWarning = false;
    companyAdditionalIncomes: CompanyAdditionalIncome[];
    companyBenefits: Benefit[];
    companyDeductions: Deduction[];
    hasEmployeesOnPayroll = true;
    ytds: Ytd[];
    ytdHeaders: YTDHeader[] = [];
    ytdColumns: Column[] = [];
    ytdRows: Row[] = [];
    ytdColumnSettings: YtdColumnSettings;
    hasAgGridFeature = false;
    hasMultiCellPasteFeature = false;
    gridApi?: GridApi;
    selectedPayPeriodText: string = '';
    finalPayPeriod?: Date;
    startDate: Date = startOfYear(new Date());

    private _columnDefs?: ColDef[];
    private _rowData?: AgGridDatum[];
    /** A list of errors by row / column */
    private agGridErrors: AgGridCell[] = [];
    /** A list of read-only cells */
    private agGridReadOnly: AgGridCell[] = [];
    /** A list of hints for ag-grid cells */
    private agGridHints: AgGridCell[] = [];
    /** A list of cells currently being saved by ag-grid */
    private agGridUpdates: AgGridCell[] = [];
    /** A list of cells being batched for bulk update */
    private agGridBulkUpdates: BulkUpdate[] = [];
    private _isSaving = false;

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        private translate: TranslateService,
        private payrollService: PayrollService,
        private http: HttpClient,
        private features: FeatureService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        super();
        this.setHotId('ytdTable');
        this.beforeChange = convertEmptyAndNegativesToZero;
        this.features
            .hasMany(FeatureFlag.ytdImporterAgGrid, FeatureFlag.ytdTableMultiCellPaste)
            .then(([hasAgGridFeature, hasMultiCellPasteFeature]) => {
                this.hasAgGridFeature = hasAgGridFeature;
                this.hasMultiCellPasteFeature = hasMultiCellPasteFeature;
            });
    }

    ngOnInit(): void {
        try {
            this.loadData();
        } catch (err) {
            this.notify.error(err.message || 'payroll.ytds.couldNotLoadEmployeeYtds');
        }
        this.setTemplateListeners(this);
        this.checkIfBenefitsAreSetUp();
        this.checkIfAPayrollHasBeenPaid();
        const storedDate = localStorage.getItem(FINAL_PAY_PERIOD_STORAGE_KEY);
        if (storedDate) {
            const parsedDate = parse(storedDate);
            if (parsedDate && isValid(parsedDate)) {
                this.finalPayPeriod = parsedDate;
            }
        }
    }

    ngOnDestroy(): void {
        this.removeWindowEventListeners();
    }

    /**
     * We need to load a lot of data to make this table work
     * Dependant calls are ordered using async await, and we call independent calls at the same time
     */
    async loadData(): Promise<void> {
        if (this.hasMultiCellPasteFeature) {
            const ytdTable = await this.http.get<{ data: YtdTable }>(PayrollResources.YtdTable).toPromise();
            this.ytdColumns = ytdTable['data'].columns.filter((column: Column) => column.column_id !== 'employee.name');
            this.ytdRows = ytdTable['data'].rows;
            this.hasEmployeesOnPayroll = !!this.ytdRows.length;
        } else {
            this.ytdColumnSettings = await this.http
                .get<YtdColumnSettings>(
                    PayrollResources.YtdColumnSettings.replace(':company', this.auth.company.id.toString())
                )
                .toPromise();

            const [, [companyBenefits], [companyAdditionalIncomes], [companyDeductions], { data: headers }] =
                await Promise.all([
                    this.setEmployeesYtds(),

                    Benefit.param('company', this.auth.company.id)
                        .with(['employees', 'employee_benefits']) // Payroll api doesn't allow for all, but 999 is close enough
                        .limit(999)
                        .get(),

                    CompanyAdditionalIncome.param('company', this.auth.company.id).where('active', true).all(),

                    Deduction.param('company', this.auth.company.id)
                        .with(['employees', 'employee_deductions'])
                        .limit(999)
                        .get(),

                    this.http.get<{ data: YTDHeader[] }>(PayrollResources.YtdHeader).toPromise(),
                ]);
            this.setRowHeaderWidth();
            this.hasEmployeesOnPayroll = !!this.ytds.length;
            this.ytdHeaders = headers;
            this.columns = buildColumns(headers);
            this.companyBenefits = companyBenefits;
            this.companyDeductions = companyDeductions;
            this.companyAdditionalIncomes = companyAdditionalIncomes;
            this.dataset = buildDataset.bind(this)();
        }
        this.isLoading = false;
    }

    async setEmployeesYtds(): Promise<void> {
        const [ytdsList] = await Ytd.with('additional_pay_items').all();
        this.rowHeaders = [];

        this.ytds = ytdsList.map((ytd: Ytd) => {
            // Set Row headers
            this.rowHeaders.push(`${ytd.fullNameInReverseOrder}`);
            // Set column settings on YTD so it knows how to generate defaults columns
            ytd.columnSettings = this.ytdColumnSettings;
            return ytd;
        });
    }

    async saveChange(change: CellChange): Promise<any> {
        const [index, columnName, oldValue, newValue, agGridRowId] = change;

        const employeeYtdToUpdate = agGridRowId
            ? this.ytds.find(({ id }) => id.toString() === agGridRowId)
            : this.ytds[index];

        if (!employeeYtdToUpdate) throw Error(`No YTD found for index ${index} and row id ${agGridRowId}`);

        // the jurisdiction column behaves differently than the other columns
        // it is a text column (not numeric) and it has a bespoke end point
        if (columnName === 'jurisdiction') {
            await this.updateJurisdiction(employeeYtdToUpdate, newValue, change);
            return;
        }

        // Check if the value has a name in the list of calculated tax
        for (const tax of Ytd.calculatedTaxValuesExposed(this.ytdColumnSettings)) {
            if (columnName === tax['prop']) {
                await this.updateCalculatedTaxValue(employeeYtdToUpdate, tax['prop'], newValue, change);
                return;
            }
        }

        // we know that we are dealing with the
        // usual "items" which are numeric
        const amount: number = Number(newValue) > 0 ? (newValue as number) : 0;

        const itemToSave = getItemFromYtd(columnName as string, employeeYtdToUpdate, this.companyAdditionalIncomes);

        itemToSave.amount = amount;

        return itemToSave
            .param('company', this.auth.company.id)
            .param('employee', employeeYtdToUpdate.hrEmployeeId)
            .save()
            .catch(() => {
                if (agGridRowId) {
                    this.highlightErrorAgGrid({ columnId: columnName as string, rowId: agGridRowId });
                } else {
                    this.onSaveFailure(itemToSave, [index, columnName, oldValue, newValue]);
                }
            });
    }

    onJurisdictionYTDSaveFailure(change: CellChange): void {
        const [index, column, , , rowId] = change;

        if (rowId) {
            this.highlightErrorAgGrid({ rowId, columnId: column as string });
        } else {
            this.highlightError(index, column as string);
        }
    }

    /**
     * Column definitions (for Ag-grid)
     */
    get columnDefs(): ColDef[] {
        if (this._columnDefs !== undefined) return this._columnDefs;

        const rowHeader: ColDef = {
            headerName: this.translate.instant('payroll.ytds.tableColumns.employee'),
            field: 'employeeName',
            editable: false,
            pinned: 'left',
            filter: 'agTextColumnFilter',
            sort: 'asc',

            // Sort by last name -> first name, disregarding case
            comparator: (valueA, valueB) => {
                const formatNameForSorting = (name: string | null | undefined): string =>
                    name?.replace(',', ' ')?.toLowerCase() ?? '';

                if (valueA === valueB) return 0;

                return formatNameForSorting(valueA) > formatNameForSorting(valueB) ? 1 : -1;
            },
        };

        let columns: ColDef<AgGridDatum>[] = [];
        if (this.hasMultiCellPasteFeature) {
            // eslint-disable-next-line camelcase
            columns = this.ytdColumns.map(({ column_id, title, read_only, type, hint }) => {
                const colDef: ColDef = this.defaultColDef(column_id, title, type, read_only);
                // Conditionally add tooltip for certain fields
                this.tooltipColDef(colDef, title, hint);
                return colDef;
            });
        } else {
            columns = this.ytdHeaders.map(({ data, title, readOnly, type }) => {
                const colDef: ColDef = this.defaultColDef(data, title, type, readOnly);
                // Conditionally add tooltip for certain fields
                this.tooltipColDef(colDef, title, this.translate.instant(`payroll.ytds.tableTooltip.${data}`));
                return colDef;
            });
        }

        this._columnDefs = [rowHeader, ...columns];

        return this._columnDefs;
    }

    /**
     * Row data (for Ag-grid)
     *
     * We add the YTD id to the row so we can get back to it later
     */
    get rowData(): AgGridDatum[] {
        if (this._rowData !== undefined) return this._rowData;
        if (this.hasMultiCellPasteFeature) {
            this._rowData = this.ytdRows.map((rowData: Row) => {
                const cells = rowData.cells;
                const nameCell = cells['employee.name'];

                if (typeof nameCell.value === 'string' || !nameCell.value.first_name) {
                    throw Error(`Employee name column contains invalid data`);
                }
                const id = rowData.row_id;

                const fullNameInReverseOrder = `${nameCell.value.last_name}, ${nameCell.value.first_name}`;

                const cellData = Object.entries(cells).reduce(
                    (acc, [key, cell]) => {
                        if (key !== 'employee.name') {
                            acc[key] = isNaN(Number(cell.value)) ? cell.value : Number(cell.value);
                        }

                        // Track hints and read-only statuses
                        if (cell.hint) {
                            this.agGridHints.push({
                                rowId: rowData.row_id.toString(),
                                columnId: key,
                                message: cell.hint,
                            });
                        }
                        if (cell.read_only) {
                            this.agGridReadOnly.push({ rowId: rowData.row_id.toString(), columnId: key });
                        }

                        return acc;
                    },
                    {} as { [key: string]: any }
                );

                return {
                    id,
                    employeeName: fullNameInReverseOrder,
                    ...cellData,
                };
            });
        } else {
            this._rowData = this.dataset.map((datum, index) => {
                const { id, fullNameInReverseOrder } = this.ytds[index];
                return { id, employeeName: fullNameInReverseOrder, ...datum };
            });
        }
        return this._rowData;
    }

    /**
     * Unique id of a row (for Ag-grid)
     */
    getRowId(params: GetRowIdParams<AgGridDatum>): string {
        return params.data.id.toString();
    }

    onGridReady(params: GridReadyEvent): void {
        this.gridApi = params.api;
    }

    highlightErrorAgGrid(cell: AgGridCell): void {
        this.agGridErrors.push(cell);
        this.gridApi?.refreshCells({ force: true });
    }

    /**
     * Whether the table is currently saving data
     */
    get isSaving(): boolean {
        if (this.hasMultiCellPasteFeature) {
            return this.agGridBulkUpdates.length > 0 || this._isSaving;
        }

        if (this.hasAgGridFeature) {
            return this.agGridUpdates.length > 0;
        }

        return this.saving;
    }

    /**
     * Whether the table contains any errors
     */
    get hasAgGridError(): boolean {
        return this.hasAgGridFeature && this.agGridErrors.length > 0;
    }

    /**
     * Whether to allow selecting (and thereby copy/pasting) multiple cells
     */
    get isRangeSelectionEnabled(): boolean {
        return this.hasMultiCellPasteFeature;
    }

    /**
     * Search for employees with matching names
     */
    onSearch(search: string | null): void {
        if (!this.gridApi) return;

        const filterModel = this.gridApi.getFilterModel();

        if (search === null) {
            delete filterModel.employeeName;
        } else {
            filterModel.employeeName = {
                filter: search,
                filterType: 'contains',
                type: 'text',
            };
        }

        this.gridApi.setFilterModel(filterModel);
    }

    formatDate(date: Date): string {
        return format(date, 'dddd, MMMM D YYYY');
    }

    async onChangeDate(date: Date): Promise<void> {
        localStorage.setItem(FINAL_PAY_PERIOD_STORAGE_KEY, date.toISOString());
    }

    private defaultColDef(
        fieldName: string,
        headerName: string,
        type: 'numeric' | 'text',
        readOnly: boolean = false
    ): ColDef<AgGridDatum> {
        return {
            field: fieldName,
            headerName: headerName,
            type: type === 'numeric' ? 'rightAligned' : [],

            // Don't let ag-grid infer cell types. We'll handle this ourselves
            cellDataType: false,

            // Allow filtering on the jurisdiction column
            // TODO: Move filter columns on backend side if this requires for multiple fields
            filter: fieldName.includes('jurisdiction'),

            editable: ({ data }): boolean => {
                const value = data && data[fieldName];
                // Hack for v1 to disable edits on unassigned items
                if (value === undefined) {
                    return false;
                }

                // Check if the individual cell should be readonly
                if (
                    this.agGridReadOnly.some(
                        ({ rowId, columnId }) => rowId === data?.id.toString() && columnId === fieldName
                    )
                ) {
                    return false;
                }

                return !readOnly;
            },

            // Flag invalid data
            valueSetter: ({ data, newValue }): boolean => {
                if (type === 'text') {
                    data[fieldName] = newValue;
                    return true;
                }

                if (newValue < 0) {
                    this.agGridErrors.push({
                        rowId: data.id.toString(),
                        columnId: fieldName,
                        message: 'payroll.ytds.negativeNumbersAreNotAllowedInThisField',
                    });
                    this.showInvalidDataNotification();
                }

                const parsedValue = Number(newValue.toString().replace(/\$|,/g, ''));

                if (isNaN(parsedValue)) {
                    this.agGridErrors.push({
                        rowId: data.id.toString(),
                        columnId: fieldName,
                        message: 'payroll.ytds.invalidDataEntered',
                    });
                    this.showInvalidDataNotification();

                    // If the new value is not a number, we'll still display it in the field
                    // so the user will understand the issue
                    data[fieldName] = newValue;
                } else {
                    data[fieldName] = parsedValue;
                }

                return true;
            },

            valueFormatter: ({ value, node, column }): string => {
                if (type !== 'numeric') return value;

                if (!value && value !== 0) {
                    // Add placeholder text, or "N/A" for non-editable cells
                    return node && column.isCellEditable(node) ? '0.00' : this.translate.instant('N/A');
                }

                // Format the number according to the current locale
                return (
                    formatLocalizedNumber(value, {
                        locale: this.translateService.currentLang as Locale,
                        decimalPlaces: 2,
                    }) ?? value.toString()
                );
            },

            onCellValueChanged: ({ node, column, oldValue, newValue }): void => {
                if (!node?.id) throw Error('No node or node id for cell update');

                if (oldValue === newValue) return;

                // Don't send update if new value is negative or NaN or something else weird
                if (type === 'numeric' && !(newValue >= 0)) {
                    return;
                }

                // Remove previous error for this cell
                this.agGridErrors = this.agGridErrors.filter(
                    ({ rowId, columnId }) => !(rowId === node.id && columnId === column.getColId())
                );
                this.refreshGridCells();

                if (this.hasMultiCellPasteFeature) {
                    const update: BulkUpdate = {
                        rowId: node.id,
                        columnId: column.getColId(),
                        value: newValue,
                    };

                    const cell = this.ytdRows.find((row) => row.row_id.toString() === node.id)?.cells[
                        column.getColId()
                    ];
                    if (cell) {
                        update.cellId = cell.cell_id?.toString();
                        update.updatedAt = cell.updated_at;
                    }

                    this.onCellUpdate(update);
                } else {
                    // Use the handsOnTable save method to update the cell
                    // When we implement bulk update, this will go away
                    this.agGridUpdates.push({ rowId: node.id, columnId: column.getColId() });
                    this.saveChange([-1, column.getColId(), oldValue, newValue, node.id]).finally(() => {
                        this.agGridUpdates = this.agGridUpdates.filter(
                            ({ rowId, columnId }) => !(rowId === node.id && columnId === column.getColId())
                        );
                    });
                }
            },

            tooltipValueGetter: ({ value, node }): string | undefined => {
                if (value === undefined) {
                    return this.translate.instant('payroll.ytds.employeeNotAssignedToItem', {
                        item: headerName,
                    });
                }

                if (!node || !node.id) return;

                // Find hint for the cell
                const hint = this.agGridHints.find(
                    ({ rowId, columnId }) => rowId === node.id && columnId === fieldName
                );
                if (hint) {
                    return hint.message;
                }

                // Fetch error message for the cell
                const error = this.agGridErrors.find(
                    ({ rowId, columnId }) => rowId === node.id && columnId === fieldName
                );
                if (error?.message) {
                    return this.translate.instant(error.message);
                }
            },

            cellClass: ({ node, column }): string | string[] => {
                if (this.agGridErrors.some(({ rowId, columnId }) => rowId === node.id && columnId === fieldName)) {
                    return 'ytd-cell-error';
                }

                if (!column.isCellEditable(node)) return 'ytd-cell-readonly';

                const value = node.data && node.data[fieldName];

                if (!value && value !== 0) return 'ytd-cell-empty';

                return 'ytd-cell-filled';
            },

            cellStyle: {
                // Align numeric cells to the right
                textAlign: type === 'numeric' ? 'right' : '',
            },
        };
    }

    private tooltipColDef(colDef: ColDef, columnName: string, hint: string | undefined): ColDef {
        const tooltipFields = ['F5B', 'CSB'];
        if (tooltipFields.includes(columnName)) {
            colDef.headerComponent = CustomHeader;
            colDef.headerComponentParams = { tooltip: hint };
        }
        return colDef;
    }

    private async checkIfBenefitsAreSetUp(): Promise<void> {
        const { isSetUp } = await this.payrollService.getPayrollBenefitsSetUpStatus();
        this.benefitsAreSetUp = isSetUp;
    }

    private async checkIfAPayrollHasBeenPaid(): Promise<void> {
        const { payrollHasBeenPaid } = await this.payrollService.getPayrollHasBeenPaidStatus();
        this.payrollHasBeenPaid = payrollHasBeenPaid;
    }

    private async updateJurisdiction(
        employeeYtdToUpdate: Ytd,
        newValue: string | number,
        change: CellChange
    ): Promise<void> {
        const jurisdictionYtd = employeeYtdToUpdate.jurisdiction;
        if (jurisdictionYtd === newValue) {
            return;
        }

        const jurisdictionPayload = {
            data: {
                type: 'paystubs',
                attributes: {
                    jurisdiction: newValue,
                },
            },
        };

        try {
            const jurisdictionUpdateUrl = PayrollResources.JurisdictionYTD.replace(
                ':employee',
                employeeYtdToUpdate.hrEmployeeId
            ).replace(':payStubId', employeeYtdToUpdate.paystubId.toString());
            await this.http.put(jurisdictionUpdateUrl, jurisdictionPayload).toPromise();
        } catch (error) {
            this.onJurisdictionYTDSaveFailure(change);
        }
    }

    private async updateCalculatedTaxValue(
        employeeYtdToUpdate: Ytd,
        name: string,
        newValue: string | number,
        change: CellChange
    ): Promise<void> {
        const oldValue = employeeYtdToUpdate.calculatedTaxValues?.[name];
        if (oldValue === newValue) {
            return;
        }

        employeeYtdToUpdate.setCalculatedTaxValue(name, newValue);
        const calculatedTaxValuesPayload = {
            data: {
                type: 'paystubs',
                attributes: {
                    // eslint-disable-next-line camelcase
                    calculated_tax_values: employeeYtdToUpdate.calculatedTaxValues,
                },
            },
        };

        try {
            const jurisdictionUpdateUrl = PayrollResources.JurisdictionYTD.replace(
                ':employee',
                employeeYtdToUpdate.hrEmployeeId
            ).replace(':payStubId', employeeYtdToUpdate.paystubId.toString());
            await this.http.put(jurisdictionUpdateUrl, calculatedTaxValuesPayload).toPromise();
        } catch (error) {
            this.onJurisdictionYTDSaveFailure(change);
        }
    }

    /**
     * Handle ag-grid cell updates
     */
    private onCellUpdate(update: BulkUpdate): void {
        this.agGridBulkUpdates.push(update);

        if (this.agGridBulkUpdates.length >= BULK_UPDATE_BATCH_SIZE_LIMIT) {
            this.sendBatchUpdates();
        }

        // After a period of inactivity, send updates even if they haven't reached the size limit
        this.sendBatchUpdatesDebounced();
    }

    /**
     * Send updates after a period of inactivity
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private sendBatchUpdatesDebounced = debounce(() => this.sendBatchUpdates(), BULK_UPDATE_DEBOUNCE_TIME);

    /**
     * Show a toast indicating that invalid error has been entered
     * This function is debounced as it may be called simultaneously for multiple cells, but should only execute once
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private showInvalidDataNotification = debounce(() => {
        this.notify.error('payroll.ytds.invalidDataEntered');
    }, 100);

    /**
     * Rerender the table to refresh cell error states
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private refreshGridCells = debounce(() => {
        this.gridApi?.refreshCells({ force: true });
    }, 100);

    /**
     * Send batched updates to the backend
     */
    private async sendBatchUpdates(): Promise<void> {
        // Only send one batch at a time
        if (this._isSaving) return;

        // The backend doesn't like empty arrays
        if (this.agGridBulkUpdates.length === 0) return;

        this._isSaving = true;

        // Get the next batch of updates
        const updates = this.agGridBulkUpdates.splice(0, BULK_UPDATE_BATCH_SIZE_LIMIT);

        try {
            // Send the updates to the backend
            const { data } = await this.http
                .put<{ data: BulkUpdateResponse[] }>(PayrollResources.YtdTable, { changes: updates })
                .toPromise();

            this.updateCells(data);
        } catch (e) {
            this.notify.error(e);

            throw e;
        } finally {
            this._isSaving = false;
        }

        // If there are more updates, send another batch
        if (this.agGridBulkUpdates.length > 0) {
            this.sendBatchUpdates();
        }
    }

    private updateCells(responses: BulkUpdateResponse[]): void {
        for (const res of responses) {
            const rowId = res.row_id.toString();
            const columnId = res.column_id;
            if (res.status === 'ok') {
                this.agGridErrors = this.agGridErrors.filter(
                    (error) => !(error.rowId === rowId && error.columnId === columnId)
                );
                const row = this.ytdRows.find((r) => r.row_id.toString() === rowId);
                if (row) {
                    const cell = row?.cells[columnId];
                    // update cell_id
                    if (cell) {
                        Object.assign(cell, {
                            cell_id: res.cell_id,
                            updated_at: res.updated_at,
                        });
                    }
                }
            } else {
                this.agGridErrors.push({ rowId, columnId, message: res.message });
            }
        }

        this.refreshGridCells();
    }
}
